/**
 * Vertical-leak guard — Phase 2 follow-on.
 *
 * Each `/features/<vertical>/config.js` declares the `routes` it owns.
 * This module turns those declarations into runtime + build-time
 * checks so a "wrong-vertical CTA mounted on wrong route" bug fails
 * loudly in dev and is gateable in CI.
 *
 * The routes arrays use simple-string patterns (`:state`, `:city`,
 * `:slug`) — we convert them to regex once at module load.
 *
 * Public API:
 *   getExpectedVertical(pathname)          → "insurance" | "auto-loan" | "refinance" | "warranty" | null
 *   assertVerticalMatchesRoute(path, ver)  → throws in dev, no-op in prod
 *   listVerticalRouteRegexes()             → debug helper
 */
import { CATEGORY_REGISTRY } from "./categoryRegistry";

function _compileRoute(pattern) {
  // Convert "/insurance/:state" → /^\/insurance\/[^/]+$/
  // Convert "/warranty/:state/:city" → /^\/warranty\/[^/]+\/[^/]+$/
  const escaped = pattern
    .replace(/\//g, "\\/")
    .replace(/:[a-zA-Z_]+/g, "[^/]+");
  return new RegExp(`^${escaped}$`);
}

const _VERTICAL_ROUTE_TABLE = Object.entries(CATEGORY_REGISTRY).map(([id, cfg]) => ({
  id,
  patterns: (cfg.routes || []).map(_compileRoute),
}));

/**
 * Return the vertical id whose declared routes match `pathname`, or
 * `null` if no vertical claims this route (e.g. /guides, /compare,
 * /admin — those are explicitly cross-vertical surfaces).
 */
export function getExpectedVertical(pathname) {
  if (!pathname) return null;
  for (const { id, patterns } of _VERTICAL_ROUTE_TABLE) {
    if (patterns.some((rx) => rx.test(pathname))) return id;
  }
  return null;
}

/**
 * Iter-275.V — Map sub-category ids to their parent vertical id.
 *
 * The CategoryRegistry uses concrete sub-categories for data routing
 * (`auto-loan-new`, `auto-loan-used`, `auto-loan-refinance` all hit
 * different lender catalogs and modal funnels). But the vertical
 * guard only knows about the four parent verticals declared by
 * `/features/<vertical>/config.js`. Normalise before comparing so a
 * concrete sub-category like `auto-loan-new` rendering on the
 * `/auto-loans` route (owned by `auto-loan`) doesn't trip a false
 * leak warning.
 */
const _SUBCATEGORY_TO_VERTICAL = {
  "auto-loan-new": "auto-loan",
  "auto-loan-used": "auto-loan",
  "auto-loan-refinance": "refinance",
};

function _normaliseVertical(id) {
  if (!id) return id;
  return _SUBCATEGORY_TO_VERTICAL[id] || id;
}

/**
 * In dev: console.warn if `actualVertical` doesn't match the
 * vertical declared as owner of `pathname`. In prod: no-op (we don't
 * spend CPU on this in user-facing code paths).
 *
 * Cross-vertical paths (return null from getExpectedVertical) always
 * pass — that's the design: /compare, /guides, /admin, the homepage,
 * and other intentionally-shared surfaces don't claim a single
 * vertical so any vertical's table is welcome there.
 */
export function assertVerticalMatchesRoute(pathname, actualVertical) {
  if (process.env.NODE_ENV === "production") return;
  const expected = getExpectedVertical(pathname);
  const actual = _normaliseVertical(actualVertical);
  if (expected && expected !== actual) {
    // eslint-disable-next-line no-console
    console.warn(
      `[vertical-leak] route ${pathname} is owned by '${expected}' but ` +
        `'${actualVertical}' rendered here. Cross-check ` +
        `/features/${actualVertical}/config.js routes[].`,
    );
  }
}

/** Debug helper — surfaces the compiled patterns so test gates can audit them. */
export function listVerticalRouteRegexes() {
  return _VERTICAL_ROUTE_TABLE.map(({ id, patterns }) => ({
    id,
    patterns: patterns.map((rx) => rx.source),
  }));
}
